import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, mergeProps as _mergeProps, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "q-gutter-sm" }

import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useQuasar, QSelect } from 'quasar';

// Stores
import { useUiStore } from '@stores/ui';
import { useSecurityStore } from '@stores/security';

// Personal components
import QPageTitle from '@components/QPageTitle.vue';
import QBtnCancel from '@components/form/QBtnCancel.vue';
import QBtnValid from '@components/form/QBtnValid.vue';
import QOfflineBanner from '@components/QOfflineBanner.vue';
import QCustomInput from '@components/form/QCustomInput.vue';

// Personal libs
import contactRepository from '@data/repository/contact.repository';
import {
  displayNotifSuccess,
  displayDialogError,
} from '@shared/lib/notify.lib';
import { IConfig } from '@shared/interface/config.interface';
import { IUser } from '@shared/interface/model.interface';
import { $t } from '@i18n/utils';
import { subjectRules } from '@shared/lib/form.lib';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { FormNameEnum } from '@shared/enum/form-name.enum';
import { InputFiedTypeEnum } from '@shared/enum/input.enum';

interface IForm {
  email: string;
  objet: string;
  message: string;
}

/**
 * Globals
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'ContactPage',
  setup(__props) {

/* eslint-disable @typescript-eslint/no-explicit-any */
// Vendor
const $q = useQuasar()

/**
 * Stores
 */
const uiStore = useUiStore();
const securityStore = useSecurityStore();

/**
 * HTML Elements
 */
const qInputEmail = ref();
const qInputMessage = ref();
const qSelectSubject = ref<QSelect>();

/**
 * Reactive data
 */
const loading = ref<boolean>(false);
const form = reactive<IForm>({
  email: '',
  objet: '',
  message: '',
});

/**
 * Computed properties
 */
const connectedUser = computed<IUser | undefined>(
  () => sessionManager.currentUser.value,
);
const isOnline = computed(() => sessionManager.isOnline.value === true);
const config = computed<IConfig | undefined>(() => uiStore.config);
const subjectList = computed<Array<string>>(
  () => config.value?.contact?.subjects ?? [],
);

/**
 * Methods
 */
function validForm(): boolean {
    // Reset focus
    qInputEmail.value?.blur()
    qInputMessage.value?.blur()
    qSelectSubject.value?.blur();

    // Validate
    qInputEmail.value?.validate()
    qSelectSubject.value?.validate();
    qInputMessage.value?.validate()

    if (qInputEmail.value != null && qInputEmail.value.hasError()) {
      qInputEmail.value.focus()
      return false;
    }

    if (qSelectSubject.value != null && qSelectSubject.value.hasError) {
      qSelectSubject.value.focus()
      return false;
    }

    if (qInputMessage.value != null && qInputMessage.value.hasError()) {
      qInputMessage.value.focus()
      return false;
    }

    return true;
}

async function onSubmit() {
  if (!validForm()) {
    return;
  }

  loading.value = true;

  contactRepository
    .postMessage({
      email: form.email,
      subject: form.objet,
      message: form.message,
      version: uiStore.changelog?.version?.frontend ?? 'inconnue',
      csrf: (await securityStore.getCsrf(FormNameEnum.CONTACT))?.token
    })
    .then(() => {
      displayNotifSuccess($t('contact.msg.success01'));
    })
    .catch((error) => {
      displayDialogError({ message: error.message ?? 'null' });
    })
    .finally(() => {
      loading.value = false;
      onReset();
    });
}

function onReset() {
  loading.value = false;

  // Reset our form values
  form.email = connectedUser.value?.email ?? '';
  form.objet = '';
  form.message = '';

  qInputEmail.value?.resetValidation();
  qInputMessage.value?.resetValidation();
  qSelectSubject.value?.resetValidation();

}

function setEmail(user?: IUser) {
  if (user?.email != null) {
    form.email = user.email;
  }
}

/**
 * Watched var
 */
watch(
  () => connectedUser.value,
  (newVal) => setEmail(newVal),
);

/**
 * Hooks
 */
onMounted(() => {
  setEmail(connectedUser.value);
});

return (_ctx: any,_cache: any) => {
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_form = _resolveComponent("q-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(QPageTitle, { title: 'Contact' }),
    _createVNode(_component_q_form, {
      class: "q-ma-md q-pa-sm form-style",
      onSubmit: onSubmit,
      onReset: onReset,
      novalidate: ""
    }, {
      default: _withCtx(() => [
        _cache[3] || (_cache[3] = _createElementVNode("div", { class: "row q-mb-none" }, [
          _createElementVNode("span", null, "* : champs obligatoires")
        ], -1 /* HOISTED */)),
        _cache[4] || (_cache[4] = _createElementVNode("div", { class: "row q-mb-md" }, [
          _createElementVNode("span", null, "# : item sélectionné")
        ], -1 /* HOISTED */)),
        _createVNode(QCustomInput, {
          modelValue: form.email,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.email) = $event)),
          ref_key: "qInputEmail",
          ref: qInputEmail,
          "input-type": _unref(InputFiedTypeEnum).EMAIL,
          disable: loading.value
        }, null, 8 /* PROPS */, ["modelValue", "input-type", "disable"]),
        _createVNode(_unref(QSelect), {
          modelValue: form.objet,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.objet) = $event)),
          options: subjectList.value,
          disable: loading.value,
          ref_key: "qSelectSubject",
          ref: qSelectSubject,
          outlined: "",
          dense: "",
          class: "q-mb-sm",
          "input-class": [
        'set-outline',
        _unref($q).dark.isActive ? 'text-white' : 'text-black',
      ],
          "aria-required": "true",
          "aria-invalid": 
        !form.objet || (form.objet && form.objet.length < 3) ? 'true' : 'false'
      ,
          "label-color": _unref($q).dark.isActive ? 'white' : 'black',
          label: `* ${_unref($t)('contact.msgSubject.label')}`,
          "hide-bottom-space": "",
          "popup-content-class": "subjectOption",
          "lazy-rules": "",
          rules: _unref(subjectRules)
        }, {
          option: _withCtx((scope) => [
            _createVNode(_component_q_item, _mergeProps(scope.itemProps, {
              class: {
            'q-item-focused': scope.focused,
            'q-item-selected': scope.selected && !scope.focused,
          }
            }), {
              default: _withCtx(() => [
                _createVNode(_component_q_item_section, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_q_item_label, null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(scope.selected ? '# ' : '') + _toDisplayString(scope.opt), 1 /* TEXT */)
                      ]),
                      _: 2 /* DYNAMIC */
                    }, 1024 /* DYNAMIC_SLOTS */)
                  ]),
                  _: 2 /* DYNAMIC */
                }, 1024 /* DYNAMIC_SLOTS */)
              ]),
              _: 2 /* DYNAMIC */
            }, 1040 /* FULL_PROPS, DYNAMIC_SLOTS */, ["class"])
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["modelValue", "options", "disable", "input-class", "aria-invalid", "label-color", "label", "rules"]),
        _createVNode(QCustomInput, {
          modelValue: form.message,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.message) = $event)),
          ref_key: "qInputMessage",
          ref: qInputMessage,
          "input-type": _unref(InputFiedTypeEnum).TEXTAREA,
          disable: loading.value
        }, null, 8 /* PROPS */, ["modelValue", "input-type", "disable"]),
        _createElementVNode("div", _hoisted_1, [
          _createVNode(QBtnCancel, {
            type: 'reset',
            label: 'Effacer',
            disable: loading.value
          }, null, 8 /* PROPS */, ["disable"]),
          _createVNode(QBtnValid, {
            type: 'submit',
            label: 'Envoyer',
            loading: loading.value,
            "icon-right": 'check',
            title: 
          isOnline.value
            ? 'Envoyer le mail'
            : _unref($t)('generic.hasToBeOnline')
        ,
            disable: loading.value || !isOnline.value
          }, null, 8 /* PROPS */, ["loading", "title", "disable"])
        ])
      ]),
      _: 1 /* STABLE */
    }),
    _createVNode(QOfflineBanner)
  ], 64 /* STABLE_FRAGMENT */))
}
}

})